import { LoadState } from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo } from 'react';

import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
export type Segment = {
  start: Dayjs;
  end: Dayjs;
  type: string;
};

export const SegmentTypes = {
  PICK_UP: 'PICK_UP',
  EN_ROUTE: 'EN_ROUTE',
  DROP_OFF: 'DROP_OFF',
  LOGGING: 'LOGGING',
};

const loadStateSegmentTypeMap = {
  [SegmentTypes.PICK_UP]: [LoadState.ARRIVED_PICKUP, LoadState.LOADED],
  [SegmentTypes.EN_ROUTE]: [LoadState.TO_DROPOFF, LoadState.TO_PICKUP],
  [SegmentTypes.DROP_OFF]: [LoadState.ARRIVED_DROPOFF, LoadState.UNLOADED],
};

export const getCycleSegmentType = (ping: LocationHistoryMinimal): string => {
  const loadState = ping?.loadState;

  for (const [type, states] of Object.entries(loadStateSegmentTypeMap)) {
    if (states.includes(loadState as LoadState)) {
      return type;
    }
  }

  return SegmentTypes.LOGGING;
};

const NO_SIGNAL_THRESHOLD = 15;

const useGetCycleSegments = (pings: LocationHistoryMinimal[]) =>
  useMemo(() => {
    const segments: LocationHistoryMinimal[][] = [[]];
    pings.forEach((p) => {
      const currentSegment: LocationHistoryMinimal[] = segments[segments.length - 1];
      if (!currentSegment.length) {
        currentSegment.push(p);
        return;
      }

      const lastPing = currentSegment[currentSegment.length - 1];

      if (
        getCycleSegmentType(lastPing) !== getCycleSegmentType(p) ||
        dayjs(p.occurredAt).diff(dayjs(lastPing.occurredAt), 'minutes') >=
          NO_SIGNAL_THRESHOLD
      ) {
        segments.push([p]);
        return;
      }

      currentSegment.push(p);
    });

    // Keep every non-empty group, including single-ping groups. A ping more than
    // NO_SIGNAL_THRESHOLD minutes from its neighbours (or whose phase differs from
    // them) lands alone in its own group; dropping those hid sparse pings that
    // exist in the data entirely from the timeline (TRE-15314). The empty seed
    // group (present only when there are no pings) is what length > 0 filters out.
    const formattedSegments: Segment[] = segments
      .filter((s) => s.length > 0)
      .map((s) => ({
        start: s[0].occurredAt,
        end: s[s.length - 1].occurredAt,
        type: getCycleSegmentType(s[0]),
      }));

    return formattedSegments;
  }, [pings.length]);

export { useGetCycleSegments };
